import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'
import useOfficialGameTexts from '../hooks/useOfficialGameTexts'
import UnitIconFrame, { resolveUnitLevelBadge } from './UnitIconFrame'

const PLUGIN_KEY = 'barracks_modified'
const PICKER_FAVORITES_KEY = 'recruitBotPickerFavorites'
const RECRUITMENT_SLOT_COUNT = 5

const AREA_TYPE = Object.freeze({
    mainCastle: 1,
    outpost: 4,
    externalKingdom: 12,
    metropolis: 22
})

const KINGDOM_ID = Object.freeze({
    greatEmpire: 0,
    burningSands: 1,
    everWinterGlacier: 2,
    firePeaks: 3,
    stormIslands: 4
})

const LEGACY_SELECTION_MAP = {
    0: '206',
    1: '229',
    2: '196',
    3: '218',
    4: '602'
}

const SECTION_DEFINITIONS = Object.freeze([
    {
        id: 'main',
        configKey: 'section:main',
        title: 'Main',
        prefix: 'Main',
        enableHelper: 'Enable automatic troop recruitment in main kingdom',
        slotHelper: 'Recruitment slot size in main kingdom',
        troopHelper: 'Select troop to recruit in main kingdom'
    },
    {
        id: 'outpost1',
        configKey: 'section:outpost1',
        title: 'Outpost1',
        prefix: 'Outpost1',
        enableHelper: 'Enable automatic troop recruitment in outpost 1',
        slotHelper: 'Recruitment slot size in outpost 1',
        troopHelper: 'Select troop to recruit in outpost 1'
    },
    {
        id: 'outpost2',
        configKey: 'section:outpost2',
        title: 'Outpost2',
        prefix: 'Outpost2',
        enableHelper: 'Enable automatic troop recruitment in outpost 2',
        slotHelper: 'Recruitment slot size in outpost 2',
        troopHelper: 'Select troop to recruit in outpost 2'
    },
    {
        id: 'outpost3',
        configKey: 'section:outpost3',
        title: 'Outpost3',
        prefix: 'Outpost3',
        enableHelper: 'Enable automatic troop recruitment in outpost 3',
        slotHelper: 'Recruitment slot size in outpost 3',
        troopHelper: 'Select troop to recruit in outpost 3'
    },
    {
        id: 'ice',
        configKey: 'section:ice',
        title: 'Ice',
        prefix: 'Ice',
        enableHelper: 'Enable automatic troop recruitment in ice kingdom',
        slotHelper: 'Recruitment slot size in ice kingdom',
        troopHelper: 'Select troop to recruit in ice kingdom'
    },
    {
        id: 'desert',
        configKey: 'section:desert',
        title: 'Desert',
        prefix: 'Desert',
        enableHelper: 'Enable automatic troop recruitment in desert kingdom',
        slotHelper: 'Recruitment slot size in desert kingdom',
        troopHelper: 'Select troop to recruit in desert kingdom'
    },
    {
        id: 'fire',
        configKey: 'section:fire',
        title: 'Fire',
        prefix: 'Fire',
        enableHelper: 'Enable automatic troop recruitment in fire kingdom',
        slotHelper: 'Recruitment slot size in fire kingdom',
        troopHelper: 'Select troop to recruit in fire kingdom'
    },
    {
        id: 'storm',
        configKey: 'section:storm',
        title: 'Storm',
        prefix: 'Storm',
        enableHelper: 'Enable automatic troop recruitment in storm kingdom',
        slotHelper: 'Recruitment slot size in storm kingdom',
        troopHelper: 'Select troop to recruit in storm kingdom'
    },
    {
        id: 'capital',
        configKey: 'section:capital',
        title: 'Capital',
        prefix: 'Capital',
        enableHelper: 'Enable automatic troop recruitment in capital (KID 0/1/2/3)',
        slotHelper: 'Recruitment slot size in capital',
        troopHelper: 'Select troop to recruit in capital'
    },
    {
        id: 'metro',
        configKey: 'section:metro',
        title: 'Metro',
        prefix: 'Metro',
        enableHelper: 'Enable automatic troop recruitment in metro (KID 0)',
        slotHelper: 'Recruitment slot size in metro',
        troopHelper: 'Select troop to recruit in metro'
    }
])

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const normalizeSearchText = value =>
    String(value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

const hasPerTargetConfig = value =>
    Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0

const normalizeSelectionOption = (option, fallbackValue) => {
    if (option && typeof option === 'object' && !Array.isArray(option)) {
        return {
            label: option.label ?? String(option.value ?? fallbackValue),
            translationKey: String(option.translationKey ?? ''),
            value: String(option.value ?? fallbackValue),
            familyValue: String(option.familyValue ?? ''),
            unitWodID: Number.isFinite(Number(option.unitWodID)) ? Number(option.unitWodID) : undefined,
            exactUnit: option.exactUnit === true,
            minBarracksLevel: Number.isFinite(Number(option.minBarracksLevel)) ? Number(option.minBarracksLevel) : 0,
            minLevel: Number.isFinite(Number(option.minLevel)) ? Number(option.minLevel) : 1,
            maxLevel: Number.isFinite(Number(option.maxLevel)) ? Number(option.maxLevel) : 1,
            level: Number.isFinite(Number(option.level)) ? Number(option.level) : undefined,
            iconUrl: String(option.iconUrl ?? ''),
            assetKey: String(option.assetKey ?? ''),
            availableLevels: Array.isArray(option.availableLevels) ?
                option.availableLevels
                    .map(level => Number.parseInt(String(level), 10))
                    .filter(Number.isFinite)
                    .sort((left, right) => left - right) :
                []
        }
    }

    return {
        label: String(option ?? fallbackValue),
        translationKey: '',
        value: String(fallbackValue),
        familyValue: '',
        unitWodID: undefined,
        exactUnit: false,
        minBarracksLevel: 0,
        minLevel: 1,
        maxLevel: 1,
        level: undefined,
        iconUrl: '',
        assetKey: '',
        availableLevels: []
    }
}

const normalizeTroopType = value => {
    if ([undefined, null, ''].includes(value))
        return ''

    const normalizedValue = String(value)
    return LEGACY_SELECTION_MAP[normalizedValue] ?? normalizedValue
}

const normalizeLevel = value => {
    if ([undefined, null, ''].includes(value))
        return ''

    const parsed = Number.parseInt(String(value), 10)
    return Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : ''
}

const normalizeBarracksLevel = value => {
    if ([undefined, null, ''].includes(value))
        return ''

    const parsed = Number.parseInt(String(value), 10)
    return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : ''
}

const normalizeSlotSize = value => {
    if ([undefined, null, ''].includes(value))
        return ''

    const parsed = Number.parseInt(String(value), 10)
    return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : ''
}

const normalizeRecruitmentSlot = value => ({
    troopType: normalizeTroopType(value?.troopType),
    level: normalizeLevel(value?.level),
    slotSize: normalizeSlotSize(value?.slotSize)
})

const hasMeaningfulRecruitmentSlot = value =>
    Boolean(value?.troopType || value?.level || value?.slotSize)

const normalizeRecruitmentSlots = value => {
    const sourceSlots = Array.isArray(value?.slots) ? value.slots : []
    const slots = Array.from({ length: RECRUITMENT_SLOT_COUNT }, (_, index) =>
        normalizeRecruitmentSlot(sourceSlots[index])
    )
    const legacySlot = normalizeRecruitmentSlot(value)

    if (hasMeaningfulRecruitmentSlot(legacySlot) && !hasMeaningfulRecruitmentSlot(slots[0]))
        slots[0] = legacySlot

    return slots
}

const normalizeTargetConfig = value => {
    const slots = normalizeRecruitmentSlots(value)
    const primarySlot = slots[0] ?? normalizeRecruitmentSlot(undefined)
    const barracksLevel = normalizeBarracksLevel(value?.barracksLevel)
    const hasSavedValues = Boolean(barracksLevel || slots.some(hasMeaningfulRecruitmentSlot))

    return {
        enabled: typeof value?.enabled === 'boolean' ? value.enabled : hasSavedValues,
        troopType: primarySlot.troopType,
        level: primarySlot.level,
        barracksLevel,
        slotSize: primarySlot.slotSize,
        slots
    }
}

const hasMeaningfulTargetConfig = value =>
    Boolean(
        value?.enabled ||
        value?.barracksLevel ||
        normalizeRecruitmentSlots(value).some(hasMeaningfulRecruitmentSlot)
    )

export const shouldActivateRecruitPlugin = castleConfigs =>
    Object.values(castleConfigs ?? {}).some(value => {
        const config = normalizeTargetConfig(value)
        return config.enabled && config.slots.some(slot => Boolean(slot.troopType))
    })

const buildFallbackTargetConfig = (legacyLevel, legacyTroopType) => {
    const troopType = normalizeTroopType(legacyTroopType)
    const level = normalizeLevel(legacyLevel)

    if (!troopType && !level)
        return undefined

    return {
        enabled: true,
        troopType,
        level,
        barracksLevel: '',
        slotSize: '',
        slots: [
            { troopType, level, slotSize: '' },
            ...Array.from({ length: RECRUITMENT_SLOT_COUNT - 1 }, () => normalizeRecruitmentSlot(undefined))
        ]
    }
}

const buildTargetFallbacks = (targets, fallbackConfig) => {
    if (!fallbackConfig)
        return {}

    return Object.fromEntries(
        targets.map(target => [target.key, { ...fallbackConfig }])
    )
}

const sortTargets = targets =>
    [...targets].sort((left, right) =>
        Number(left?.type !== AREA_TYPE.mainCastle) - Number(right?.type !== AREA_TYPE.mainCastle) ||
        Number(left?.type !== AREA_TYPE.externalKingdom) - Number(right?.type !== AREA_TYPE.externalKingdom) ||
        Number(left?.x ?? 0) - Number(right?.x ?? 0) ||
        Number(left?.y ?? 0) - Number(right?.y ?? 0) ||
        String(left?.label ?? '').localeCompare(String(right?.label ?? ''))
    )

const loadFavorites = () => {
    if (typeof window === 'undefined')
        return []

    try {
        const parsedValue = JSON.parse(window.localStorage.getItem(PICKER_FAVORITES_KEY) ?? '[]')
        return Array.isArray(parsedValue) ? parsedValue.map(value => String(value)).filter(Boolean) : []
    }
    catch {
        return []
    }
}

const saveFavorites = favoriteKeys => {
    if (typeof window === 'undefined')
        return

    try {
        window.localStorage.setItem(PICKER_FAVORITES_KEY, JSON.stringify(favoriteKeys))
    }
    catch { }
}

const getTargetSearchText = target =>
    normalizeSearchText(`${target?.label ?? ''} ${target?.nameCandidate ?? ''} ${target?.detail ?? ''}`)

const isCapitalTarget = target => /\bcapital\b/.test(getTargetSearchText(target))
const isMetroTarget = target =>
    (
        Number(target?.kingdomID) === KINGDOM_ID.greatEmpire &&
        [AREA_TYPE.externalKingdom, AREA_TYPE.metropolis].includes(Number(target?.type))
    ) ||
    /\bmetro\b/.test(getTargetSearchText(target))

const pickKingdomTarget = (targets, kingdomID) =>
    sortTargets(
        (Array.isArray(targets) ? targets : [])
            .filter(target => Number(target?.kingdomID) === kingdomID)
            .filter(target => Number(target?.type) !== AREA_TYPE.outpost)
            .filter(target => !isCapitalTarget(target))
            .filter(target => !isMetroTarget(target))
    )[0] ?? null

export const buildSectionTargets = targets => {
    const normalizedTargets = Array.isArray(targets) ? targets : []
    const empireTargets = normalizedTargets.filter(target => Number(target?.kingdomID) === KINGDOM_ID.greatEmpire)
    const empireOutposts = sortTargets(empireTargets.filter(target => Number(target?.type) === AREA_TYPE.outpost))

    return {
        main: sortTargets(
            empireTargets
                .filter(target => Number(target?.type) === AREA_TYPE.mainCastle)
                .filter(target => !isCapitalTarget(target))
                .filter(target => !isMetroTarget(target))
        )[0] ?? null,
        outpost1: empireOutposts[0] ?? null,
        outpost2: empireOutposts[1] ?? null,
        outpost3: empireOutposts[2] ?? null,
        ice: pickKingdomTarget(normalizedTargets, KINGDOM_ID.everWinterGlacier),
        desert: pickKingdomTarget(normalizedTargets, KINGDOM_ID.burningSands),
        fire: pickKingdomTarget(normalizedTargets, KINGDOM_ID.firePeaks),
        storm: pickKingdomTarget(normalizedTargets, KINGDOM_ID.stormIslands),
        capital: sortTargets(normalizedTargets.filter(isCapitalTarget))[0] ?? null,
        metro: sortTargets(normalizedTargets.filter(isMetroTarget))[0] ?? null
    }
}

export const buildLevelOptions = option => {
    if (Array.isArray(option?.availableLevels) && option.availableLevels.length > 0)
        return [...new Set(
            option.availableLevels
                .map(level => Number.parseInt(String(level), 10))
                .filter(Number.isFinite)
        )]
            .sort((left, right) => left - right)
            .map(level => String(level))

    const minLevel = Number(option?.minLevel)
    const maxLevel = Number(option?.maxLevel)

    if (!Number.isFinite(minLevel) || !Number.isFinite(maxLevel))
        return []

    const start = Math.min(minLevel, maxLevel)
    const end = Math.max(minLevel, maxLevel)
    const levels = []

    for (let level = start; level <= end; level += 1)
        levels.push(String(level))

    return levels
}

export const buildBarracksLevelOptions = selectionOptions => {
    const maxLevel = Math.max(
        1,
        ...(Array.isArray(selectionOptions) ? selectionOptions : [])
            .map(option => Number(option?.minBarracksLevel))
            .filter(Number.isFinite)
    )
    const levels = []

    for (let level = 1; level <= maxLevel; level += 1)
        levels.push(String(level))

    return levels
}

export const resolveDisplayedLevel = (currentLevel, option) => {
    const levels = buildLevelOptions(option)
    if (levels.length === 0)
        return ''

    const normalizedCurrentLevel = normalizeLevel(currentLevel)
    const hasPositiveLevel = levels.some(level => Number(level) > 0)
    if (!levels.includes(normalizedCurrentLevel))
        return levels[levels.length - 1]
    if (normalizedCurrentLevel === '0' && hasPositiveLevel)
        return levels[levels.length - 1]

    return normalizedCurrentLevel
}

export const filterOptionsByBarracksLevel = (selectionOptions, barracksLevel) => {
    const normalizedOptions = (Array.isArray(selectionOptions) ? selectionOptions : [])
        .map((option, index) => normalizeSelectionOption(option, index))
    const normalizedBarracksLevel = Number.parseInt(String(barracksLevel ?? ''), 10)

    if (!Number.isFinite(normalizedBarracksLevel) || normalizedBarracksLevel <= 0)
        return normalizedOptions

    return normalizedOptions.filter(option => option.minBarracksLevel <= normalizedBarracksLevel)
}

export const applyBarracksLevelToConfig = (currentConfig, barracksLevel, filteredOptions, allOptionsByValue) => {
    const nextConfig = normalizeTargetConfig({
        ...currentConfig,
        barracksLevel
    })

    const slots = nextConfig.slots.map(slot => {
        if (!slot.troopType)
            return slot

        const selectedOption = filteredOptions.find(option => option.value === slot.troopType) ??
            allOptionsByValue.get(slot.troopType)

        if (!filteredOptions.some(option => option.value === slot.troopType)) {
            return {
                ...slot,
                troopType: '',
                level: ''
            }
        }

        return {
            ...slot,
            level: resolveDisplayedLevel(slot.level, selectedOption)
        }
    })

    return normalizeTargetConfig({
        ...nextConfig,
        slots
    })
}

export const resolveTargetMaxSlotSize = target => {
    const value = Number(target?.defaultSlotSize)
    return Number.isFinite(value) && value > 0 ? String(value) : ''
}

export const resolveSlotSizeMode = slotSize =>
    normalizeSlotSize(slotSize) ? 'custom' : 'auto'

export const applySlotSizeModeToConfig = (currentConfig, nextMode, target) => {
    const nextConfig = normalizeTargetConfig(currentConfig)
    const slots = [...nextConfig.slots]
    const primarySlot = slots[0] ?? normalizeRecruitmentSlot(undefined)

    if (nextMode === 'custom') {
        slots[0] = {
            ...primarySlot,
            slotSize: primarySlot.slotSize || resolveTargetMaxSlotSize(target) || '1'
        }

        return normalizeTargetConfig({
            ...nextConfig,
            slots
        })
    }

    slots[0] = {
        ...primarySlot,
        slotSize: ''
    }

    return normalizeTargetConfig({
        ...nextConfig,
        slots
    })
}

export const updateRecruitmentSlotConfig = (currentConfig, slotIndex, updater) => {
    const nextConfig = normalizeTargetConfig(currentConfig)
    const slots = [...nextConfig.slots]
    const normalizedIndex = Math.max(0, Math.min(RECRUITMENT_SLOT_COUNT - 1, Number(slotIndex) || 0))
    slots[normalizedIndex] = normalizeRecruitmentSlot(updater(slots[normalizedIndex] ?? normalizeRecruitmentSlot(undefined)))

    return normalizeTargetConfig({
        ...nextConfig,
        slots
    })
}

export const applySlotSizeModeToSlot = (currentConfig, slotIndex, nextMode, target) =>
    updateRecruitmentSlotConfig(currentConfig, slotIndex, currentSlot => {
        if (nextMode === 'custom') {
            return {
                ...currentSlot,
                slotSize: currentSlot.slotSize || resolveTargetMaxSlotSize(target) || '1'
            }
        }

        return {
            ...currentSlot,
            slotSize: ''
        }
    })

const createFallbackSectionTarget = section => ({
    key: section.configKey,
    sectionId: section.id,
    barracksState: 'unknown',
    isFallbackTarget: true
})

export const resolveRecruitSectionDisplayName = (section, translate = value => value) => {
    const genericLabel = String(translate(section?.title ?? '') ?? '').trim()
    const castleName = String(section?.target?.nameCandidate ?? section?.target?.castleName ?? '').trim()

    return castleName && castleName.toLocaleLowerCase() !== genericLabel.toLocaleLowerCase() ?
        `${genericLabel} · ${castleName}` :
        genericLabel
}

const FIELD_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 42,
        borderRadius: '10px',
        backgroundColor: 'rgba(24, 36, 57, 0.76)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

function Helper({ children, color = '#95a5c4' }) {
    return (
        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.8 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color }} />
            <Typography sx={{ fontSize: '0.89rem', color, lineHeight: 1.35 }}>{children}</Typography>
        </Stack>
    )
}

function FieldTitle({ children }) {
    return (
        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
            {children}
        </Typography>
    )
}

function ToggleField({ checked, onChange, label, helper, disabled = false }) {
    return (
        <Box sx={{ minHeight: 54 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                <Switch
                    checked={checked}
                    disabled={disabled}
                    onChange={event => onChange(event.target.checked)}
                    sx={{
                        width: 48,
                        height: 29,
                        p: 0,
                        '& .MuiSwitch-switchBase': {
                            p: '2.5px',
                            '&.Mui-checked': {
                                transform: 'translateX(18px)',
                                color: '#ffffff'
                            },
                            '&.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#2f7cff',
                                opacity: 1
                            }
                        },
                        '& .MuiSwitch-thumb': {
                            width: 24,
                            height: 24,
                            boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                            opacity: 1,
                            borderRadius: '999px',
                            backgroundColor: 'rgba(111, 123, 143, 0.9)'
                        }
                    }}
                />
                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                    {label}
                </Typography>
            </Stack>
            <Helper>{helper}</Helper>
        </Box>
    )
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>{children}</Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

export const buildRecruitPickerItems = ({ targetOptions, selectedOption, selectedLevel, getTroopLabel }) => {
    const itemsByKey = new Map()
    const addItem = (option, level) => {
        if (!option?.value)
            return

        const normalizedLevel = normalizeLevel(level)
        const key = `troop:${option.value}:level:${normalizedLevel || 'auto'}`
        if (itemsByKey.has(key))
            return

        const displayId = option.unitWodID ? `#${option.unitWodID}` : option.value
        const label = `${displayId}: ${getTroopLabel(option)}`
        const item = {
            key,
            id: option.value,
            label,
            itemType: 'troop',
            level: normalizedLevel,
            minBarracksLevel: Number.isFinite(Number(option.minBarracksLevel)) ? Number(option.minBarracksLevel) : 0,
            searchText: [
                label,
                option.unitWodID,
                normalizedLevel ? `level ${normalizedLevel}` : '',
                option.minBarracksLevel ? `barracks ${option.minBarracksLevel}` : ''
            ].filter(Boolean).join(' ')
        }
        if (option.unitWodID)
            item.unitWodID = option.unitWodID
        if (option.iconUrl)
            item.iconUrl = option.iconUrl
        if (option.assetKey)
            item.assetKey = option.assetKey

        itemsByKey.set(key, item)
    }

    ;(Array.isArray(targetOptions) ? targetOptions : []).forEach(option => {
        if (option?.exactUnit || option?.unitWodID) {
            addItem(option, option.level ?? option.availableLevels?.[0] ?? selectedLevel)
            return
        }

        const levels = buildLevelOptions(option)
        if (levels.length === 0) {
            addItem(option, selectedLevel)
            return
        }

        levels.forEach(level => addItem(option, level))
    })

    if (selectedOption)
        addItem(selectedOption, resolveDisplayedLevel(selectedLevel, selectedOption) || selectedLevel)

    return [...itemsByKey.values()].sort((left, right) =>
        String(left.label ?? '').localeCompare(String(right.label ?? '')) ||
        Number(left.level || 0) - Number(right.level || 0)
    )
}

function TroopPickerDialog({ open, items, onClose, onSelect, t }) {
    const [query, setQuery] = React.useState('')
    const [favoritesOnly, setFavoritesOnly] = React.useState(false)
    const [favoriteKeys, setFavoriteKeys] = React.useState(() => loadFavorites())
    const favoriteKeySet = React.useMemo(() => new Set(favoriteKeys), [favoriteKeys])

    React.useEffect(() => {
        if (!open)
            return

        setQuery('')
        setFavoritesOnly(false)
    }, [open])

    React.useEffect(() => {
        saveFavorites(favoriteKeys)
    }, [favoriteKeys])

    const filteredItems = React.useMemo(() => {
        const normalizedQuery = normalizeSearchText(query)

        return items
            .filter(entry => {
                if (favoritesOnly && !favoriteKeySet.has(entry.key))
                    return false
                if (!normalizedQuery)
                    return true

                return normalizeSearchText(`${entry.label} ${entry.id} ${entry.searchText}`).includes(normalizedQuery)
            })
            .sort((left, right) => {
                const leftFavorite = favoriteKeySet.has(left.key)
                const rightFavorite = favoriteKeySet.has(right.key)
                if (leftFavorite !== rightFavorite)
                    return leftFavorite ? -1 : 1

                return String(left.label ?? '').localeCompare(String(right.label ?? ''))
            })
    }, [favoriteKeySet, favoritesOnly, items, query])

    const toggleFavorite = itemKey => {
        setFavoriteKeys(currentKeys =>
            currentKeys.includes(itemKey) ?
                currentKeys.filter(currentKey => currentKey !== itemKey) :
                [...currentKeys, itemKey]
        )
    }

    const handleClearFilters = () => {
        setQuery('')
        setFavoritesOnly(false)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 20px), 566px)',
                    maxWidth: '566px',
                    minHeight: '724px',
                    maxHeight: 'min(calc(100vh - 20px), 724px)',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: 'linear-gradient(180deg, rgba(39,51,74,0.98) 0%, rgba(31,43,66,0.98) 100%)',
                    border: '1px solid rgba(122, 143, 180, 0.32)',
                    boxShadow: '0 26px 60px rgba(0,0,0,0.42)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(3, 8, 18, 0.68)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.2, pt: 2.05, pb: 1.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <SearchRoundedIcon sx={{ fontSize: 28, color: '#f4f7ff' }} />
                        <Typography sx={{ fontSize: '1.16rem', fontWeight: 800, color: '#f5f7ff' }}>
                            {t('Troop Picker')}
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ color: '#9aa7c2', width: 30, height: 30 }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 2.2, pt: 0, pb: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Stack direction="row" spacing={1.1} sx={{ mb: 1.5 }}>
                    <TextField
                        fullWidth
                        placeholder={t('Search by name or ID...')}
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                minHeight: 42,
                                borderRadius: '10px',
                                backgroundColor: 'rgba(28, 41, 65, 0.9)',
                                '& fieldset': {
                                    borderColor: 'rgba(57, 121, 248, 0.95)',
                                    borderWidth: '2px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                }
                            },
                            '& .MuiInputBase-input': {
                                color: '#edf3ff',
                                fontSize: '0.98rem',
                                fontWeight: 600
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: '#a8b4cd', fontSize: 24 }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={favoriteKeys.length > 0 || favoritesOnly ? <FavoriteRoundedIcon sx={{ color: favoritesOnly ? '#ff8ea0' : '#ffffff' }} /> : <FavoriteBorderRoundedIcon />}
                        onClick={() => setFavoritesOnly(current => !current)}
                        sx={{
                            minWidth: 122,
                            minHeight: 42,
                            borderRadius: '10px',
                            borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.72)' : 'rgba(112, 136, 174, 0.34)',
                            backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.14)' : 'rgba(42, 54, 78, 0.56)',
                            color: '#f3f6ff',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.96rem',
                            '&:hover': {
                                borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.82)' : 'rgba(112, 136, 174, 0.5)',
                                backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.2)' : 'rgba(50, 63, 88, 0.7)'
                            }
                        }}
                    >
                        {t('Favorites')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CloseRoundedIcon />}
                        onClick={handleClearFilters}
                        sx={{
                            minWidth: 108,
                            minHeight: 42,
                            borderRadius: '10px',
                            backgroundColor: '#ff3345',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 800,
                            fontSize: '0.96rem',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#ff4858',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {t('Clear')}
                    </Button>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.55 }}>
                    <Typography sx={{ color: '#95a5c4', fontSize: '0.92rem' }}>
                        {t('Recruit Bot only uses troops. Use Tool Build Settings for workshop tools.')}
                    </Typography>
                    <Typography sx={{ color: '#b3bfd7', fontWeight: 700, fontSize: '0.98rem', flexShrink: 0 }}>
                        {`${filteredItems.length} ${t('results')}`}
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        borderRadius: '12px',
                        border: '1px solid rgba(123, 147, 186, 0.28)',
                        backgroundColor: 'rgba(31, 43, 67, 0.92)',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                >
                    {filteredItems.map((item, index) => {
                        const isFavorite = favoriteKeySet.has(item.key)
                        return (
                            <Box
                                key={item.key}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    onSelect(item)
                                    onClose()
                                }}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        onSelect(item)
                                        onClose()
                                    }
                                }}
                                sx={{
                                    px: 2.15,
                                    py: 1.6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.2,
                                    cursor: 'pointer',
                                    borderBottom: index < filteredItems.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.03)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                                    <UnitIconFrame
                                        iconUrl={item.iconUrl}
                                        itemType="troop"
                                        level={resolveUnitLevelBadge(item)}
                                        size={52}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.9} sx={{ mb: 0.6 }}>
                                            <Typography sx={{ color: '#5ea3ff', fontWeight: 800, fontSize: '1.01rem' }}>
                                                {`#${index + 1}`}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    px: 0.95,
                                                    minHeight: 28,
                                                    borderRadius: '10px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid rgba(130, 147, 177, 0.45)',
                                                    color: '#d6deec',
                                                    fontWeight: 700,
                                                    fontSize: '0.94rem'
                                                }}
                                            >
                                                {t('Troop')}
                                            </Box>
                                        </Stack>

                                        <Typography sx={{ color: '#f5f7ff', fontWeight: 700, fontSize: '1.14rem', lineHeight: 1.18 }}>
                                            {item.label}
                                        </Typography>
                                        {item.level || item.minBarracksLevel ? (
                                            <Typography sx={{ mt: 0.55, color: '#95a5c4', fontSize: '0.92rem' }}>
                                                {[
                                                    item.level ? `${t('Level')} ${item.level}` : '',
                                                    item.minBarracksLevel ? `${t('Barracks')} ${item.minBarracksLevel}+` : ''
                                                ].filter(Boolean).join(' | ')}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Box>

                                <IconButton
                                    onClick={event => {
                                        event.stopPropagation()
                                        toggleFavorite(item.key)
                                    }}
                                    sx={{ color: isFavorite ? '#ff6e82' : '#9aa6bf', width: 34, height: 34 }}
                                >
                                    {isFavorite ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                </IconButton>
                            </Box>
                        )
                    })}
                </Box>

                <Box
                    sx={{
                        mt: 1.45,
                        px: 2.2,
                        py: 1.45,
                        mx: -2.2,
                        borderTop: '1px solid rgba(255,255,255,0.08)'
                    }}
                >
                    <Typography sx={{ color: '#7f8aa3', fontSize: '0.94rem' }}>
                        {t('Click to select | Favorite to bookmark | Search by troop name, ID, or level')}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function TroopField({ label, helper, value, level, iconUrl = '', onOpen, disabled = false, warning = '' }) {
    return (
        <Box>
            <FieldTitle>{label}</FieldTitle>
            <TextField
                fullWidth
                value={value}
                placeholder={helper}
                onClick={disabled ? undefined : onOpen}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={disabled ? undefined : onOpen} edge="end" sx={{ color: '#d9e4fb' }} disabled={disabled}>
                                <SearchRoundedIcon />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                sx={FIELD_INPUT_SX}
                disabled={disabled}
            />
            {value ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <UnitIconFrame iconUrl={iconUrl} itemType="troop" level={level} size={75} />
                    <Chip label="Troop" size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#c3d2eb' }} />
                    {level ? <Chip label={`Level ${level}`} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#8fa3c8' }} /> : null}
                </Stack>
            ) : null}
            <Helper color={warning ? '#f6c36b' : '#95a5c4'}>{warning || helper}</Helper>
        </Box>
    )
}

export default function RecruitBotSettingsPanel({
    userPlugins,
    onChange,
    availableTargets,
    plugins,
    appLanguage = 'en',
    bthMode = false
}) {
    const [pickerTarget, setPickerTarget] = React.useState(null)

    const pluginState = React.useMemo(
        () => userPlugins?.[PLUGIN_KEY] && typeof userPlugins[PLUGIN_KEY] === 'object' ? userPlugins[PLUGIN_KEY] : {},
        [userPlugins]
    )
    const rawConfigs = React.useMemo(
        () => pluginState?.castleConfigs && typeof pluginState.castleConfigs === 'object' ? pluginState.castleConfigs : {},
        [pluginState]
    )
    const normalizedTargets = React.useMemo(
        () => (Array.isArray(availableTargets) ? availableTargets : [])
            .map(target => ({ ...target, key: String(target?.key ?? '') }))
            .filter(target => target.key),
        [availableTargets]
    )

    const sectionTargets = React.useMemo(() => buildSectionTargets(normalizedTargets), [normalizedTargets])
    const sections = React.useMemo(
        () => (bthMode ? SECTION_DEFINITIONS.filter(section => section.id === 'main') : SECTION_DEFINITIONS).map(section => ({
            ...section,
            target: sectionTargets[section.id] ?? createFallbackSectionTarget(section)
        })),
        [bthMode, sectionTargets]
    )

    const pluginDefinition = React.useMemo(
        () => (Array.isArray(plugins) ? plugins : []).find(plugin => plugin?.key === PLUGIN_KEY),
        [plugins]
    )
    const selectionDefinition = pluginDefinition?.pluginOptions?.find(option => option?.type === 'BarracksTargets' || option?.key === 'castleConfigs')
    const allOptions = React.useMemo(
        () => (selectionDefinition?.selection ?? []).map((option, index) => normalizeSelectionOption(option, index)),
        [selectionDefinition]
    )
    const allOptionsByValue = React.useMemo(
        () => new Map(allOptions.map(option => [option.value, option])),
        [allOptions]
    )
    const barracksLevelOptions = React.useMemo(
        () => buildBarracksLevelOptions(allOptions),
        [allOptions]
    )

    const troopLabelTexts = React.useMemo(
        () => [...new Set(
            allOptions
                .map(option => String(option?.label ?? '').trim())
                .filter(Boolean)
        )],
        [allOptions]
    )
    const troopTranslationKeys = React.useMemo(
        () => [...new Set(
            allOptions
                .map(option => String(option?.translationKey ?? '').trim())
                .filter(Boolean)
        )],
        [allOptions]
    )

    const t = useAutoTranslate(appLanguage, [
        'Recruit Bot Settings',
        'Configure automatic troop recruitment for all kingdoms and outposts',
        'Targets are loading from the live bot status. Open this section again after the bot syncs your castles.',
        'Live castle sync is optional. You can configure sections now and the bot will bind them automatically after the first successful login.',
        'This section is not synced yet. The bot will bind it automatically when castle data becomes available.',
        'Barracks data is still loading for this location.',
        'No barracks found in this location yet.',
        'Saved selection is no longer valid for this location.',
        'Selected troop is not recruitable right now in this location.',
        'No troops are available for the selected barracks level.',
        'No troops are recruitable here right now.',
        'Request Alliance Help',
        'Request alliance help after recruitment starts in this location',
        'Slot Size',
        'Slot Size Mode',
        'Recruitment Slot',
        'Clear Slot',
        'Enable Recruitment',
        'Troop ID',
        'Troop Picker',
        'Search by name or ID...',
        'Favorites',
        'Clear',
        'Max',
        'Custom',
        'results',
        'Troop',
        'Level',
        'Barracks',
        'Recruit Bot only uses troops. Use Tool Build Settings for workshop tools.',
        'Click to select | Favorite to bookmark | Search by troop name, ID, or level',
        'Enable automatic troop recruitment in main kingdom',
        'Recruitment slot size in main kingdom',
        'Select troop to recruit in main kingdom',
        'Enable automatic troop recruitment in outpost 1',
        'Recruitment slot size in outpost 1',
        'Select troop to recruit in outpost 1',
        'Enable automatic troop recruitment in outpost 2',
        'Recruitment slot size in outpost 2',
        'Select troop to recruit in outpost 2',
        'Enable automatic troop recruitment in outpost 3',
        'Recruitment slot size in outpost 3',
        'Select troop to recruit in outpost 3',
        'Enable automatic troop recruitment in ice kingdom',
        'Recruitment slot size in ice kingdom',
        'Select troop to recruit in ice kingdom',
        'Enable automatic troop recruitment in desert kingdom',
        'Recruitment slot size in desert kingdom',
        'Select troop to recruit in desert kingdom',
        'Enable automatic troop recruitment in fire kingdom',
        'Recruitment slot size in fire kingdom',
        'Select troop to recruit in fire kingdom',
        'Enable automatic troop recruitment in storm kingdom',
        'Recruitment slot size in storm kingdom',
        'Select troop to recruit in storm kingdom',
        'Enable automatic troop recruitment in capital (KID 0/1/2/3)',
        'Recruitment slot size in capital',
        'Select troop to recruit in capital',
        'Enable automatic troop recruitment in metro (KID 0)',
        'Recruitment slot size in metro',
        'Select troop to recruit in metro',
        'Choose whether to use the live maximum slot size or a custom amount.',
        'Auto uses the current maximum slot size for this location.',
        'Auto uses the maximum slot size after live castle sync is available.',
        'Custom slot size per free recruitment slot.',
        'Custom sends exactly the number you type, like the recruit field in game.',
        'Use Auto to always fill every free slot with the maximum available amount.',
        'Current live maximum',
        'Any troop can be preselected here. Live castle data only warns whether the current location can recruit it right now.',
        'Auto keeps the full troop catalog visible. Live castle data is used only for warnings in this location.',
        'Configure up to five recruitment slots. The bot fills free barracks slots in this order, then starts again from slot 1.',
        ...SECTION_DEFINITIONS.map(section => section.title),
        ...troopLabelTexts
    ])
    const getOfficialGameText = useOfficialGameTexts(appLanguage, troopTranslationKeys)
    const getTroopLabel = React.useCallback(option => {
        const fallbackLabel = String(option?.label ?? '')
        const translationKey = String(option?.translationKey ?? '').trim()
        return (translationKey ? getOfficialGameText(translationKey, '') : '') || t(fallbackLabel)
    }, [getOfficialGameText, t])

    const fallbackConfig = React.useMemo(
        () => buildFallbackTargetConfig(pluginState?.level, pluginState?.troopType),
        [pluginState?.level, pluginState?.troopType]
    )
    const requestAllianceHelp = pluginState?.requestAllianceHelp !== false

    const updatePluginSettings = React.useCallback(updater => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}
        updater(nextPlugins[PLUGIN_KEY])
        onChange(nextPlugins)
    }, [onChange, userPlugins])

    const getDisplayedConfig = React.useCallback(section => {
        if (!section)
            return normalizeTargetConfig(undefined)

        if (hasPerTargetConfig(rawConfigs)) {
            const storedValue = rawConfigs[section.configKey] ??
                rawConfigs[section.target?.key] ??
                rawConfigs[String(section.target?.areaID ?? '')]
            if (storedValue)
                return normalizeTargetConfig(storedValue)
        }

        return normalizeTargetConfig(fallbackConfig)
    }, [fallbackConfig, rawConfigs])

    const getLiveTargetOptions = React.useCallback(section => {
        const target = section?.target
        if (Array.isArray(target?.availableRecruitmentOptions))
            return target.availableRecruitmentOptions.map((option, index) => normalizeSelectionOption(option, index))

        const barracksLevel = Number(target?.barracksLevel)
        if (Number.isFinite(barracksLevel) && barracksLevel > 0)
            return filterOptionsByBarracksLevel(allOptions, barracksLevel)

        return []
    }, [allOptions])

    const getTargetOptions = React.useCallback((section, config = normalizeTargetConfig(undefined)) => {
        const liveOptions = getLiveTargetOptions(section)
        if (liveOptions.length > 0)
            return liveOptions

        if (config?.barracksLevel)
            return filterOptionsByBarracksLevel(allOptions, config.barracksLevel)

        return allOptions
    }, [allOptions, getLiveTargetOptions])

    const updateTargetConfig = React.useCallback((section, updater) => {
        if (!section)
            return

        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}

        const nextConfigs = hasPerTargetConfig(rawConfigs) ?
            Object.fromEntries(
                Object.entries(rawConfigs).map(([key, value]) => [String(key), normalizeTargetConfig(value)])
            ) :
            buildTargetFallbacks(normalizedTargets, fallbackConfig)

        if (bthMode) {
            Object.keys(nextConfigs).forEach(key => {
                if (key !== 'section:main' && key !== String(section.target?.key ?? '') && key !== String(section.target?.areaID ?? ''))
                    delete nextConfigs[key]
            })
        }

        const existingConfig = nextConfigs[section.configKey] ??
            nextConfigs[section.target?.key] ??
            nextConfigs[String(section.target?.areaID ?? '')]
        const updatedConfig = normalizeTargetConfig(updater(normalizeTargetConfig(existingConfig)))
        if (hasMeaningfulTargetConfig(updatedConfig))
            nextConfigs[section.configKey] = updatedConfig
        else
            delete nextConfigs[section.configKey]

        nextPlugins[PLUGIN_KEY].castleConfigs = nextConfigs
        if (shouldActivateRecruitPlugin(nextConfigs))
            nextPlugins[PLUGIN_KEY].state = true
        onChange(nextPlugins)
    }, [bthMode, fallbackConfig, normalizedTargets, onChange, rawConfigs, userPlugins])

    const sectionCards = React.useMemo(() => sections.map(section => {
        const config = getDisplayedConfig(section)
        const targetOptions = getTargetOptions(section, config)
        const liveTargetOptions = getLiveTargetOptions(section)
        const maxSlotSize = resolveTargetMaxSlotSize(section.target)
        const sectionWarning = (() => {
            if (section.target?.isFallbackTarget)
                return t('Any troop can be preselected here. Live castle data only warns whether the current location can recruit it right now.')
            if (section.target?.barracksState === 'loading')
                return t('Barracks data is still loading for this location.')
            if (section.target?.barracksState === 'missing')
                return t('No barracks found in this location yet.')
            if (config.barracksLevel && targetOptions.length === 0)
                return t('No troops are available for the selected barracks level.')
            if (!config.barracksLevel && section.target?.barracksState === 'ready' && liveTargetOptions.length === 0)
                return t('No troops are recruitable here right now.')
            return ''
        })()
        const slots = config.slots.map((slotConfig, slotIndex) => {
            const selectedOption = targetOptions.find(option => option.value === slotConfig.troopType) ??
                targetOptions.find(option => String(option.unitWodID ?? '') === String(slotConfig.troopType)) ??
                allOptionsByValue.get(slotConfig.troopType)
            const selectedIdLabel = selectedOption?.unitWodID ? `#${selectedOption.unitWodID}` : selectedOption?.value
            const selectedLabel = selectedOption ? `${selectedIdLabel}: ${getTroopLabel(selectedOption)}` : ''
            const levelOptions = buildLevelOptions(selectedOption)
            const selectedLevel = selectedOption ? resolveDisplayedLevel(slotConfig.level, selectedOption) : slotConfig.level
            const slotSizeMode = resolveSlotSizeMode(slotConfig.slotSize)
            const hasInvalidSavedSelection = Boolean(slotConfig.troopType) && !selectedOption
            const hasLiveUnavailableSelection =
                !config.barracksLevel &&
                Boolean(slotConfig.troopType) &&
                liveTargetOptions.length > 0 &&
                !liveTargetOptions.some(option =>
                    option.value === slotConfig.troopType ||
                    String(option.unitWodID ?? '') === String(slotConfig.troopType)
                )
            let warning = sectionWarning
            if (hasInvalidSavedSelection)
                warning = t('Saved selection is no longer valid for this location.')
            else if (hasLiveUnavailableSelection)
                warning = t('Selected troop is not recruitable right now in this location.')

            return {
                index: slotIndex,
                number: slotIndex + 1,
                config: slotConfig,
                selectedOption,
                selectedLabel,
                selectedLevel,
                levelOptions,
                slotSizeMode,
                displayedSlotSize: slotSizeMode === 'custom' ? slotConfig.slotSize : '',
                warning
            }
        })

        return {
            ...section,
            config,
            targetOptions,
            maxSlotSize,
            sectionWarning,
            slots
        }
    }), [allOptionsByValue, getDisplayedConfig, getLiveTargetOptions, getTargetOptions, getTroopLabel, sections, t])

    const pickerSection = sectionCards.find(section => section.id === pickerTarget?.sectionId)
    const pickerSlot = pickerSection?.slots?.[pickerTarget?.slotIndex]
    const pickerItems = React.useMemo(() => {
        if (!pickerSection || !pickerSlot)
            return []

        return buildRecruitPickerItems({
            targetOptions: pickerSection.targetOptions,
            selectedOption: pickerSlot.selectedOption,
            selectedLevel: pickerSlot.config.level,
            getTroopLabel
        })
    }, [getTroopLabel, pickerSection, pickerSlot])

    return (
        <Paper
            sx={{
                p: { xs: 2.2, md: 3.1 },
                borderRadius: '22px',
                border: '1px solid rgba(129, 146, 178, 0.18)',
                background: 'linear-gradient(180deg, rgba(27, 40, 63, 0.9) 0%, rgba(24, 35, 56, 0.86) 100%)',
                boxShadow: '0 22px 46px rgba(7, 13, 26, 0.22)'
            }}
        >
            <Stack spacing={3.2}>
                <Box>
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 0.55 }}>
                        <PersonAddAlt1RoundedIcon sx={{ color: '#f5f7ff', fontSize: 22 }} />
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.42rem', fontWeight: 800 }}>
                            {t(bthMode ? 'BTH Recruit Bot Settings' : 'Recruit Bot Settings')}
                        </Typography>
                    </Stack>
                    <Typography sx={{ color: '#9fb0cc', fontSize: '1.02rem' }}>
                        {t(bthMode ? 'Configure automatic troop recruitment for the BTH main castle' : 'Configure automatic troop recruitment for all kingdoms and outposts')}
                    </Typography>
                    {normalizedTargets.length === 0 ? (
                        <Helper>{t('Live castle sync is optional. You can configure sections now and the bot will bind them automatically after the first successful login.')}</Helper>
                    ) : null}
                    {pluginState?.state !== true ? (
                        <Alert
                            severity="warning"
                            sx={{
                                mt: 1.8,
                                borderRadius: '14px',
                                backgroundColor: 'rgba(105, 78, 18, 0.22)',
                                color: '#f8e7b2',
                                border: '1px solid rgba(245, 194, 66, 0.28)',
                                '& .MuiAlert-icon': {
                                    color: '#f6c455'
                                }
                            }}
                        >
                            {t('Recruit Bot is currently off. Saving at least one enabled troop selection here will switch it on automatically.')}
                        </Alert>
                    ) : null}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.8 }}>
                    <ToggleField
                        checked={requestAllianceHelp}
                        onChange={value => updatePluginSettings(pluginConfig => {
                            pluginConfig.requestAllianceHelp = value
                        })}
                        label={t('Request Alliance Help')}
                        helper={t('Request alliance help after recruitment starts in this location')}
                    />
                </Box>

                {sectionCards.map(section => (
                    <Box key={section.id}>
                        <SectionHeading>{resolveRecruitSectionDisplayName(section, t)}</SectionHeading>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.8 }}>
                            <Stack spacing={2.2}>
                                <ToggleField
                                    checked={section.config.enabled}
                                    disabled={false}
                                    onChange={value => updateTargetConfig(section, current => ({ ...current, enabled: value }))}
                                    label={`${section.prefix}: ${t('Enable Recruitment')}`}
                                    helper={t(section.enableHelper)}
                                />
                                <Box>
                                    <FieldTitle>{`${section.prefix}: ${t('Barracks Level')}`}</FieldTitle>
                                    <TextField
                                        select
                                        fullWidth
                                        value={section.config.barracksLevel}
                                        onChange={event => {
                                            const nextBarracksLevel = normalizeBarracksLevel(event.target.value)
                                            const filteredOptions = nextBarracksLevel ?
                                                filterOptionsByBarracksLevel(allOptions, nextBarracksLevel) :
                                                getTargetOptions(section, { ...section.config, barracksLevel: '' })

                                            updateTargetConfig(section, current => applyBarracksLevelToConfig(
                                                current,
                                                nextBarracksLevel,
                                                filteredOptions,
                                                allOptionsByValue
                                            ))
                                        }}
                                        sx={FIELD_INPUT_SX}
                                    >
                                        <MenuItem value="">
                                            {Number(section.target?.barracksLevel) > 0 ?
                                                `${t('Auto')} (${t('Live')} ${section.target.barracksLevel})` :
                                                t('Auto')
                                            }
                                        </MenuItem>
                                        {barracksLevelOptions.map(level => (
                                            <MenuItem key={`${section.id}-barracks-${level}`} value={level}>
                                                {level}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <Helper>
                                        {section.config.barracksLevel ?
                                            t('The troop picker is filtered by the selected barracks level.') :
                                            Number(section.target?.barracksLevel) > 0 ?
                                                t('Auto keeps the full troop catalog visible. Live castle data is used only for warnings in this location.') :
                                                t('Choose a barracks level first if you want to preview troops for a future upgrade.')
                                        }
                                    </Helper>
                                </Box>
                            </Stack>

                            <Stack spacing={2.2}>
                                <Helper color={section.sectionWarning ? '#f6c36b' : '#95a5c4'}>
                                    {section.sectionWarning ||
                                        t('Configure up to five recruitment slots. The bot fills free barracks slots in this order, then starts again from slot 1.')}
                                </Helper>
                            </Stack>
                        </Box>

                        <Stack spacing={1.6} sx={{ mt: 2.1 }}>
                            {section.slots.map(slot => (
                                <Paper
                                    key={`${section.id}-slot-${slot.number}`}
                                    sx={{
                                        p: 1.6,
                                        borderRadius: '16px',
                                        border: '1px solid rgba(129, 146, 178, 0.16)',
                                        backgroundColor: 'rgba(16, 27, 46, 0.42)'
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.3 }}>
                                        <Typography sx={{ color: '#f5f7ff', fontWeight: 850 }}>
                                            {`${section.prefix}: ${t('Recruitment Slot')} ${slot.number}`}
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={() => updateTargetConfig(section, current =>
                                                updateRecruitmentSlotConfig(current, slot.index, () => ({
                                                    troopType: '',
                                                    level: '',
                                                    slotSize: ''
                                                }))
                                            )}
                                            sx={{ color: '#9fb0cc', textTransform: 'none', fontWeight: 800 }}
                                        >
                                            {t('Clear Slot')}
                                        </Button>
                                    </Stack>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(220px, 1.5fr) repeat(3, minmax(120px, 1fr))' }, gap: 1.5 }}>
                                        <TroopField
                                            label={t('Troop')}
                                            helper={t(section.troopHelper)}
                                            value={slot.selectedLabel}
                                            level={slot.selectedLevel}
                                            iconUrl={slot.selectedOption?.iconUrl}
                                            disabled={section.targetOptions.length === 0 && !slot.selectedOption}
                                            warning={slot.warning}
                                            onOpen={() => setPickerTarget({ sectionId: section.id, slotIndex: slot.index })}
                                        />
                                        <Box>
                                            <FieldTitle>{t('Level')}</FieldTitle>
                                            <TextField
                                                select
                                                fullWidth
                                                value={slot.selectedLevel}
                                                onChange={event => updateTargetConfig(section, current =>
                                                    ({
                                                        ...updateRecruitmentSlotConfig(current, slot.index, currentSlot => ({
                                                            ...currentSlot,
                                                            level: event.target.value
                                                        })),
                                                        enabled: true
                                                    })
                                                )}
                                                sx={FIELD_INPUT_SX}
                                                disabled={slot.levelOptions.length === 0 || !slot.selectedOption}
                                            >
                                                {slot.levelOptions.length === 0 ? (
                                                    <MenuItem value="">-</MenuItem>
                                                ) : slot.levelOptions.map(level => (
                                                    <MenuItem key={`${section.id}-slot-${slot.number}-level-${level}`} value={level}>
                                                        {level}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <Helper>{t('Choose which available troop level to recruit in this location')}</Helper>
                                        </Box>
                                        <Box>
                                            <FieldTitle>{t('Slot Size Mode')}</FieldTitle>
                                            <TextField
                                                select
                                                fullWidth
                                                value={slot.slotSizeMode}
                                                onChange={event => updateTargetConfig(section, current =>
                                                    applySlotSizeModeToSlot(current, slot.index, event.target.value, section.target)
                                                )}
                                                sx={FIELD_INPUT_SX}
                                            >
                                                <MenuItem value="auto">
                                                    {section.maxSlotSize ?
                                                        `${t('Auto')} (${t('Max')} ${section.maxSlotSize})` :
                                                        t('Auto')
                                                    }
                                                </MenuItem>
                                                <MenuItem value="custom">{t('Custom')}</MenuItem>
                                            </TextField>
                                            <Helper>
                                                {slot.slotSizeMode === 'custom' ?
                                                    t('Custom slot size per free recruitment slot.') :
                                                    section.maxSlotSize ?
                                                        `${t('Auto uses the current maximum slot size for this location.')} ${t('Max')}: ${section.maxSlotSize}.` :
                                                        t('Auto uses the maximum slot size after live castle sync is available.')
                                                }
                                            </Helper>
                                        </Box>
                                        <Box>
                                            <FieldTitle>{t('Slot Size')}</FieldTitle>
                                            <TextField
                                                fullWidth
                                                value={slot.displayedSlotSize}
                                                onChange={event => updateTargetConfig(section, current =>
                                                    updateRecruitmentSlotConfig(current, slot.index, currentSlot => ({
                                                        ...currentSlot,
                                                        slotSize: event.target.value
                                                    }))
                                                )}
                                                sx={FIELD_INPUT_SX}
                                                disabled={slot.slotSizeMode !== 'custom'}
                                                placeholder={section.maxSlotSize || ''}
                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                            />
                                            <Helper>
                                                {slot.slotSizeMode === 'custom' ?
                                                    section.maxSlotSize ?
                                                        `${t('Current live maximum')}: ${section.maxSlotSize}. ${t('Custom sends exactly the number you type, like the recruit field in game.')}` :
                                                        t('Custom sends exactly the number you type, like the recruit field in game.') :
                                                    t(section.slotHelper)
                                                }
                                            </Helper>
                                        </Box>
                                    </Box>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>
                ))}
            </Stack>

            <TroopPickerDialog
                open={Boolean(pickerSection && pickerSlot)}
                items={pickerItems}
                onClose={() => setPickerTarget(null)}
                onSelect={entry => {
                    if (!pickerSection || !pickerSlot)
                        return

                    const matchedOption = pickerSection.targetOptions.find(option => option.value === entry.id) ??
                        allOptionsByValue.get(entry.id)

                    updateTargetConfig(pickerSection, current => ({
                        ...updateRecruitmentSlotConfig(current, pickerSlot.index, currentSlot => ({
                            ...currentSlot,
                            troopType: entry.id,
                            level: resolveDisplayedLevel(entry.level || currentSlot.level, matchedOption)
                        })),
                        enabled: true
                    }))
                    setPickerTarget(null)
                }}
                t={t}
            />
        </Paper>
    )
}
